import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiStatus, NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { ShipTimeStatus } from 'inventory/enums';
import { quotePartService } from 'quotes/services';
import { ILeadTimeSuggestionsPayload } from 'quotes/types';

import { useShipTimeChecker } from './useShipTimeChecker';

export const useGetLeadTimeSuggestions = () => {
  const [lastRequestedPartId, setLastRequestedPartId] = useState<number>();
  const [leadTime, setLeadTime] = useState();

  const { t } = useTranslation();
  const { clearNotification, showNotification } = useNotificationContext();
  const { checkShipTimeStatus } = useShipTimeChecker();

  const loadLeadTimeSuggestions = useCallback(
    async (payload: ILeadTimeSuggestionsPayload, shipTimeChecked = false) => {
      try {
        if (lastRequestedPartId === payload?.quotePartId) {
          return;
        }

        setLastRequestedPartId(payload?.quotePartId);
        setLeadTime(null);

        showNotification({
          type: ApiStatus.LOADING,
          title: t('titleStatus'),
          message: t('messageLoadingLeadTimeSuggestions'),
          position: NotificationPosition.FORM,
        });

        const result = await quotePartService.getLeadTime(payload?.quotePartId);

        if (result?.leadTime?.shipTimeStatus === ShipTimeStatus.FINISHED || shipTimeChecked) {
          setLeadTime(result?.leadTime);
          clearNotification();

          return;
        }

        checkShipTimeStatus(
          payload,
          () => loadLeadTimeSuggestions(payload, true),
          (error?) => {
            showNotification({
              title: t('errorTitleLeadTimeSuggestionsLoading'),
              message: error?.message || t('serverError'),
              type: ApiStatus.ERROR,
              position: NotificationPosition.FORM,
            });
          }
        );
      } catch (error) {
        showNotification({
          title: t('errorTitleLeadTimeSuggestionsLoading'),
          message: (error as Error)?.message || t('serverError'),
          type: ApiStatus.ERROR,
          position: NotificationPosition.FORM,
        });
      }
    },
    [checkShipTimeStatus, clearNotification, lastRequestedPartId, showNotification, t]
  );

  return {
    leadTime,
    loadLeadTimeSuggestions,
  };
};
